import { faEdit, faTrashAlt } from '@fortawesome/pro-regular-svg-icons'
import { Button, ButtonAdd, Icon, Modal, Toast } from '@seiue/ui'
import { Collapse } from 'antd'
import { useState, useContext } from 'react'
import styled from 'styled-components'

import {
  CourseOutlineTask,
  Outline,
  OutlineReq,
  courseApi,
} from 'packages/sdks-next/growp'

import { getTheme } from '@/utils/theme'

import { EditCourseTaskContext } from '../context'

import { DraggableTasks } from './DraggableTasks'
import { EditChapterNameModal } from './EditChapterNameModal'
import { EditTask } from './EditTask'

const { Panel } = Collapse

/**
 * 课程章节
 *
 * @param param0 - 参数
 * @param param0.outline - 课程章节
 * @returns 组件
 */
export const OutlineItem: React.FC<{
  outline: OutlineReq
}> = ({ outline }) => {
  const [isCreating, setIsCreating] = useState<boolean>(false)
  const { outlines, curTaskId } = useContext(EditCourseTaskContext)

  const { api: deleteCourseOutline } = courseApi.useDeleteCourseOutline({
    reload: [courseApi.loadCourseActivityTask],
  })

  const theme = getTheme()

  return (
    <StyleCollapse
      bordered={false}
      defaultActiveKey="1"
    >
      <Panel
        header={
          <Header>
            <HeaderLeft>
              <HeaderTitle>{outline.name}</HeaderTitle>
              <Icon
                icon={faEdit}
                color={theme.text._3}
                className="edit_icon"
                onClick={(e) => {
                  e.stopPropagation()
                  const { hide } = Modal.open({
                    title: '编辑课程章节',
                    children: (
                      <EditChapterNameModal
                        outlines={outlines}
                        hide={() => hide()}
                        outline={outline}
                        activityTaskId={curTaskId}
                      />
                    ),
                    width: 480,
                    footer: null,
                    bodyStyle: { padding: 0 },
                  })
                }}
              />
            </HeaderLeft>
            <div>
              <HeaderLabel className="task_count_label">{`共 ${
                outline.tasks?.length || 0
              } 项课程任务`}</HeaderLabel>
              <Button
                icon={faTrashAlt}
                type="text"
                className="delete_outline_button"
                onClick={async (e) => {
                  e.stopPropagation()
                  Modal.confirm({
                    title: '确定要删除此课程章节吗？',
                    content: `正在删除课程章节「${outline.name}」，章节内的课程任务数据将被清空，确认要继续吗？`,
                    onOk: async () => {
                      if (outline.id) {
                        await deleteCourseOutline(outline.id)
                        Toast.success('删除成功')
                      }
                    },
                    okText: '确定',
                  })
                }}
                style={{ minWidth: 0 }}
              >
                {'删除'}
              </Button>
            </div>
          </Header>
        }
        key="1"
        style={{ padding: 0 }}
      >
        <DraggableTasks
          tasks={(outline.tasks || []) as CourseOutlineTask[]}
          outlines={outlines as unknown as Outline[]}
          outlineId={outline.id}
        />
        {isCreating ? (
          <EditTask
            close={() => setIsCreating(false)}
            outlineId={outline.id}
            taskLength={outline.tasks?.length || 0}
          />
        ) : null}
        <AddButton>
          <ButtonAdd
            onClick={() => {
              setIsCreating(true)
            }}
          >
            {'添加课程任务'}
          </ButtonAdd>
        </AddButton>
      </Panel>
    </StyleCollapse>
  )
}
const StyleCollapse = styled(Collapse)`
  &&& {
    .ant-collapse-header {
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
    }
    .ant-collapse-expand-icon {
      margin-top: 5px;
    }
    .ant-collapse-content-box {
      padding-top: 0;
    }
  }
`
const AddButton = styled.div`
  padding: 8px 64px 8px 50px;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  background-color: ${(props) => props.theme.background._4};
  &&& {
    .se-button__link {
      font-weight: 400;
    }
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  .edit_icon,
  .delete_outline_button {
    display: none;
  }
  &:hover {
    .edit_icon,
    .delete_outline_button {
      display: block;
    }
    .task_count_label {
      display: none;
    }
  }
`

const HeaderTitle = styled.div`
  font-weight: 500;
  color: ${(props) => props.theme.text._1};
  font-size: 14px;
`

const HeaderLeft = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const HeaderLabel = styled.div`
  font-size: 14px;
  font-weight: normal;
  line-height: 32px;
  color: ${(props) => props.theme.text._3};
`
